import React, { useState, useEffect } from 'react';

const AcademicSystemConfig = ({ rules, onUpdate, loading, disabled = false }) => {
  const [formData, setFormData] = useState({
    gradingType: 'percentage',
    ciePassingPercentage: 40,
    endExamPassingPercentage: 35,
    overallPassingPercentage: 40,
    requireBothToPass: false,
    gradeThresholds: [],
    cgpaConfig: {
      maxCGPA: 10,
      passingCGPA: 5.0
    },
    percentageConfig: {
      distinctionPercentage: 75
    },
    academicSystem: {
      systemType: 'semester',
      creditSystemEnabled: false,
      minCredits: 0,
      maxCredits: 0,
      allowDecimalCredits: false,
      totalDegreeCredits: 0
    },
    roundingMode: 'nearestHalf' // default (backend expects 'nearestHalf', 'nearestInt', 'floor', 'ceil')
  });
  
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form data when rules change
  useEffect(() => {
    if (rules) {
      setFormData({
        gradingType: rules.gradingType || 'percentage',
        ciePassingPercentage: rules.ciePassingPercentage ?? 40,
        endExamPassingPercentage: rules.endExamPassingPercentage ?? 35,
        overallPassingPercentage: rules.overallPassingPercentage ?? 40,
        requireBothToPass: rules.requireBothToPass || false,
        gradeThresholds: rules.gradeThresholds || [],
        cgpaConfig: rules.cgpaConfig || {
          maxCGPA: 10,
          passingCGPA: 5.0
        },
        percentageConfig: rules.percentageConfig || {
          distinctionPercentage: 75
        },
        academicSystem: rules.academicSystem || {
          systemType: 'semester',
          creditSystemEnabled: false,
          minCredits: 0,
          maxCredits: 0,
          allowDecimalCredits: false,
          totalDegreeCredits: 0
        },
        roundingMode: rules.roundingMode || 'nearestHalf'
      });
      setHasChanges(false);
    }
  }, [rules]);

  const handleSave = async () => {
    if (!hasChanges || disabled) return;

    const updatedRules = {
      ...formData,
      tenantId: rules?.tenantId
    };
    
    const success = await onUpdate(updatedRules);
    if (success) {
      setHasChanges(false);
    }
  };

  const handleChange = (field, value) => {
    if (disabled) return;
    setHasChanges(true);
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    if (disabled) return;
    setHasChanges(true);
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const addGradeThreshold = () => {
    setHasChanges(true);
    setFormData(prev => ({
      ...prev,
      gradeThresholds: [
        ...prev.gradeThresholds,
        { grade: '', minPercentage: 0, maxPercentage: 0, gradePoint: 0, isPassing: true }
      ]
    }));
  };

  const updateGradeThreshold = (index, field, value) => {
    setHasChanges(true);
    const updated = [...formData.gradeThresholds];
    updated[index][field] = value;
    
    // Auto-adjust ranges
    if (field === 'minPercentage' && index > 0) {
      updated[index - 1].maxPercentage = value - 1;
    }
    if (field === 'maxPercentage' && index < updated.length - 1) {
      updated[index + 1].minPercentage = value + 1;
    }
    
    setFormData(prev => ({ ...prev, gradeThresholds: updated }));
  };

  const removeGradeThreshold = (index) => {
    setHasChanges(true);
    const updated = [...formData.gradeThresholds];
    updated.splice(index, 1);
    setFormData(prev => ({ ...prev, gradeThresholds: updated }));
  };

  const sectionHeader = (title, subtitle) => (
    <div className="mb-6">
      <h3 className="text-lg font-bold text-gray-900 tracking-tight">{title}</h3>
      <p className="text-sm text-gray-500">{subtitle}</p>
    </div>
  );

  const inputStyle = "w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-700 focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all outline-none disabled:opacity-50 disabled:cursor-not-allowed";

  const { gradingType, gradeThresholds } = formData;

  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-6 border-b border-gray-100">
        <div>
          <h2 className="text-2xl font-black text-gray-900 tracking-tight">Grading & Assessment Rules</h2>
          <p className="text-gray-500 text-sm">Define passing criteria, grading type, and grade thresholds</p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-100 rounded-full animate-pulse">
              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
              <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Unsaved Changes</span>
            </div>
          )}
          {disabled && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-100 rounded-full">
              <span className="w-2 h-2 bg-red-400 rounded-full"></span>
              <span className="text-xs font-bold text-red-700 uppercase tracking-wider">Read Only</span>
            </div>
          )}
        </div>
      </div>

      {/* Section 1: Academic Calendar */}
      <section>
        {sectionHeader("Academic Calendar", "Set your institution's academic structure")}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">System Type</label>
            <select
              value={formData.academicSystem.systemType}
              onChange={(e) => handleNestedChange('academicSystem', 'systemType', e.target.value)}
              disabled={disabled}
              className={inputStyle}
            >
              <option value="semester">Semester System</option>
              <option value="annual">Annual System</option>
              <option value="trimester">Trimester System</option>
              <option value="quarter">Quarter System</option>
            </select>
          </div>

          <div className="flex items-center">
            <label className="relative flex items-center gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100 w-full cursor-pointer hover:bg-white transition-colors group">
              <input
                type="checkbox"
                checked={formData.academicSystem.creditSystemEnabled}
                onChange={(e) => handleNestedChange('academicSystem', 'creditSystemEnabled', e.target.checked)}
                disabled={disabled}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-all cursor-pointer"
              />
              <div>
                <span className="block text-sm font-bold text-gray-800 group-hover:text-blue-600 transition-colors">Enable Credit System</span>
                <span className="block text-xs text-gray-400 font-medium">Subjects will carry credit weightage</span>
              </div>
            </label>
          </div>
        </div>

        {formData.academicSystem.creditSystemEnabled && (
          <div className="mt-8 p-8 bg-blue-50/50 rounded-3xl border border-blue-100">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" /></svg>
              </div>
              <h4 className="font-bold text-blue-900">Credit Configuration</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-xs font-bold text-blue-800/60 uppercase tracking-widest mb-2">Min Credits per Subject</label>
                <input
                  type="number"
                  value={formData.academicSystem.minCredits}
                  onChange={(e) => handleNestedChange('academicSystem', 'minCredits', parseInt(e.target.value) || 0)}
                  disabled={disabled}
                  className="w-full bg-white border border-blue-200 rounded-xl px-4 py-2.5 text-blue-900 focus:ring-4 focus:ring-blue-500/20 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-blue-800/60 uppercase tracking-widest mb-2">Max Credits per Subject</label>
                <input
                  type="number"
                  value={formData.academicSystem.maxCredits}
                  onChange={(e) => handleNestedChange('academicSystem', 'maxCredits', parseInt(e.target.value) || 0)}
                  disabled={disabled}
                  className="w-full bg-white border border-blue-200 rounded-xl px-4 py-2.5 text-blue-900 focus:ring-4 focus:ring-blue-500/20 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-blue-800/60 uppercase tracking-widest mb-2">Total Degree Credits</label>
                <input
                  type="number"
                  value={formData.academicSystem.totalDegreeCredits}
                  onChange={(e) => handleNestedChange('academicSystem', 'totalDegreeCredits', parseInt(e.target.value) || 0)}
                  disabled={disabled}
                  className="w-full bg-white border border-blue-200 rounded-xl px-4 py-2.5 text-blue-900 focus:ring-4 focus:ring-blue-500/20 outline-none"
                />
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Section 2: Grading Type */}
      <section className="pt-6 border-t border-gray-100">
        {sectionHeader("Grading System", "Choose how you want to evaluate students")}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { value: 'percentage', label: 'Percentage (%)', desc: 'Simple percentage marks (0-100%)', icon: '📊' },
            { value: 'grade', label: 'Letter Grades', desc: 'A, B, C, D, F with grade mapping', icon: '📝' },
            { value: 'cgpa', label: 'CGPA', desc: 'Cumulative Grade Point Average (0-10)', icon: '⭐' }
          ].map(option => (
            <label
              key={option.value}
              className={`flex items-start gap-4 p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                gradingType === option.value
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <input
                type="radio"
                value={option.value}
                checked={gradingType === option.value}
                onChange={(e) => handleChange('gradingType', e.target.value)}
                disabled={disabled}
                className="mt-1 w-4 h-4"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{option.icon}</span>
                  <span className="font-bold text-gray-900">{option.label}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{option.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* Section: Rounding Policy */}
      <section className="pt-6 border-t border-gray-100">
        {sectionHeader("Rounding Policy", "Choose how final marks should be rounded before publishing")}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              Rounding Mode
            </label>
            <select
              value={formData.roundingMode}
              onChange={e => handleChange('roundingMode', e.target.value)}
              disabled={disabled}
              className={inputStyle}
            >
              <option value="nearestHalf">Round to nearest 0.5</option>
              <option value="nearestInt">Round to nearest 1.0</option>
              <option value="floor">Always round down (floor)</option>
              <option value="ceil">Always round up (ceil)</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              <b>Examples:</b><br/>
              Student raw marks: <b>78.36</b><br/>
              {formData.roundingMode === 'nearestHalf' && <>Round to 0.5: <b>78.5</b></>}
              {formData.roundingMode === 'nearestInt' && <>Round to 1.0: <b>78</b></>}
              {formData.roundingMode === 'floor' && <>Floor: <b>78</b></>}
              {formData.roundingMode === 'ceil' && <>Ceil: <b>79</b></>}
              <br/>
              Student raw marks: <b>78.7</b><br/>
              {formData.roundingMode === 'nearestHalf' && <>Round to 0.5: <b>78.5</b></>}
              {formData.roundingMode === 'nearestInt' && <>Round to 1.0: <b>79</b></>}
              {formData.roundingMode === 'floor' && <>Floor: <b>78</b></>}
              {formData.roundingMode === 'ceil' && <>Ceil: <b>79</b></>}
            </p>
          </div>
          <div className="flex items-center mt-6">
            <input
              type="checkbox"
              checked={formData.roundingMode !== 'none'}
              onChange={e => handleChange('roundingMode', e.target.checked ? 'half' : 'none')}
              disabled={disabled}
              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
            />
            <span className="text-sm text-gray-700">Enable rounding (checkbox: e.g., Accept Terms & Conditions, Subscribe to newsletter)</span>
          </div>
        </div>
      </section>

      {/* Section 3: Passing Criteria - SIMPLE, NO WEIGHTAGES */}
      <section className="pt-6 border-t border-gray-100">
        {sectionHeader("Passing Criteria", "Define what scores are needed to pass")}
        
        <div className="bg-gray-50 rounded-2xl p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                            Minimum Attendance Required (%)
                            <span className="text-gray-400 ml-1">(Eligibility)</span>
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.minimumAttendanceRequired ?? 75}
                            onChange={e => handleChange('minimumAttendanceRequired', parseInt(e.target.value) || 0)}
                            disabled={disabled}
                            className={inputStyle}
                          />
                          <p className="text-xs text-gray-400 mt-1">Student must meet this attendance % to be eligible for exams</p>
                        </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                CIE Passing %
                <span className="text-gray-400 ml-1">(Internal exams)</span>
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.ciePassingPercentage}
                onChange={(e) => handleChange('ciePassingPercentage', parseInt(e.target.value) || 0)}
                disabled={disabled}
                className={inputStyle}
              />
              <p className="text-xs text-gray-400 mt-1">Minimum % needed in internals</p>
            </div>
            
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                End Exam Passing %
                <span className="text-gray-400 ml-1">(Final exam)</span>
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.endExamPassingPercentage}
                onChange={(e) => handleChange('endExamPassingPercentage', parseInt(e.target.value) || 0)}
                disabled={disabled}
                className={inputStyle}
              />
              <p className="text-xs text-gray-400 mt-1">Minimum % needed in final exam</p>
            </div>
            
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Overall Passing %
                <span className="text-gray-400 ml-1">(Combined score)</span>
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.overallPassingPercentage}
                onChange={(e) => handleChange('overallPassingPercentage', parseInt(e.target.value) || 0)}
                disabled={disabled}
                className={inputStyle}
              />
              <p className="text-xs text-gray-400 mt-1">Minimum average to pass subject</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 pt-2">
            <input
              type="checkbox"
              id="requireBoth"
              checked={formData.requireBothToPass}
              onChange={(e) => handleChange('requireBothToPass', e.target.checked)}
              disabled={disabled}
              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="requireBoth" className="text-sm font-medium text-gray-700">
              Student must pass BOTH CIE and End Exam separately
            </label>
          </div>

          <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm">
            <span className="font-medium">📌 Note:</span> Overall percentage is calculated as simple average of CIE and End Exam marks (if both exist). If only one exam exists, that score is used.
          </div>
        </div>
      </section>

      {/* Section 4: Grade Thresholds (Only for Grade/CGPA systems) */}
      {(gradingType === 'grade' || gradingType === 'cgpa') && (
        <section className="pt-6 border-t border-gray-100">
          <div className="flex justify-between items-center mb-4">
            {sectionHeader("Grade Thresholds", "Define percentage ranges for each grade")}
            {!disabled && (
              <button
                onClick={addGradeThreshold}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-xl hover:bg-blue-700 transition-colors"
              >
                + Add Grade
              </button>
            )}
          </div>
          
          {gradeThresholds.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
              <p className="text-yellow-700">No grade thresholds defined. Click "Add Grade" to create grade mappings.</p>
              <p className="text-xs text-yellow-600 mt-2">Example: 90-100% = A+ (10 points)</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Grade</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Min %</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Max %</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Points</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Pass/Fail</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {gradeThresholds.map((threshold, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={threshold.grade}
                          onChange={(e) => updateGradeThreshold(index, 'grade', e.target.value.toUpperCase())}
                          disabled={disabled}
                          placeholder="A+"
                          className="w-20 px-2 py-1 border rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={threshold.minPercentage}
                          onChange={(e) => updateGradeThreshold(index, 'minPercentage', parseInt(e.target.value))}
                          disabled={disabled}
                          className="w-24 px-2 py-1 border rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={threshold.maxPercentage}
                          onChange={(e) => updateGradeThreshold(index, 'maxPercentage', parseInt(e.target.value))}
                          disabled={disabled}
                          className="w-24 px-2 py-1 border rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          step="0.1"
                          value={threshold.gradePoint}
                          onChange={(e) => updateGradeThreshold(index, 'gradePoint', parseFloat(e.target.value))}
                          disabled={disabled}
                          className="w-20 px-2 py-1 border rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <select
                          value={threshold.isPassing ? 'pass' : 'fail'}
                          onChange={(e) => updateGradeThreshold(index, 'isPassing', e.target.value === 'pass')}
                          disabled={disabled}
                          className="px-2 py-1 border rounded text-sm"
                        >
                          <option value="pass">Pass</option>
                          <option value="fail">Fail</option>
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        {!disabled && (
                          <button
                            onClick={() => removeGradeThreshold(index)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm">
            <strong>💡 Tip:</strong> Grade ranges should be continuous and not overlap. 
            Example: 90-100 = A, 80-89 = B, 70-79 = C, etc.
          </div>
        </section>
      )}

      {/* Section 5: Grading Settings (Based on type) */}
      <section className="pt-6 border-t border-gray-100">
        {gradingType === 'percentage' && (
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              Distinction Percentage
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.percentageConfig.distinctionPercentage}
              onChange={(e) => handleNestedChange('percentageConfig', 'distinctionPercentage', parseInt(e.target.value) || 0)}
              disabled={disabled}
              className={inputStyle}
            />
            <p className="text-xs text-gray-400 mt-1">Above this percentage = Distinction/Honors</p>
          </div>
        )}

        {gradingType === 'cgpa' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Maximum CGPA</label>
              <select
                value={formData.cgpaConfig.maxCGPA}
                onChange={(e) => handleNestedChange('cgpaConfig', 'maxCGPA', parseInt(e.target.value))}
                disabled={disabled}
                className={inputStyle}
              >
                <option value={4}>4.0 Scale</option>
                <option value={10}>10.0 Scale</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Minimum Passing CGPA</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="10"
                value={formData.cgpaConfig.passingCGPA}
                onChange={(e) => handleNestedChange('cgpaConfig', 'passingCGPA', parseFloat(e.target.value) || 0)}
                disabled={disabled}
                className={inputStyle}
              />
            </div>
          </div>
        )}
      </section>

      {/* Save Button */}
      <div className="pt-8 flex flex-col sm:flex-row justify-end items-center gap-6 border-t-2 border-gray-100">
        {!hasChanges && !disabled && (
          <span className="text-xs font-bold text-gray-300 uppercase tracking-wider">All changes saved</span>
        )}
        <button
          onClick={handleSave}
          disabled={loading || !hasChanges || disabled}
          className={`w-full sm:w-auto px-10 py-4 rounded-2xl font-bold text-sm uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3 ${
            hasChanges && !disabled
              ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 hover:bg-blue-700 active:scale-95'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
          }`}
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              Saving...
            </>
          ) : disabled ? (
            "Permission Required"
          ) : (
            "Save Grading Rules"
          )}
        </button>
      </div>
    </div>
  );
};

export default AcademicSystemConfig;